import useDictionary from 'lib/sitecore/use-dictionary';
import { classnames } from 'tailwindcss-classnames';
import { getSalesforceFlowData, SalesForceData } from 'lib/sitecore/sitecore-cdp';
import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import useCdpLoaded from 'lib/util/useCdpLoaded';

export interface MyProfileProps {
  fields?: {};
}

export type myProfileData = {
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  email?: string;
  phone?: string;
  fax?: string;
  company?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

const MyProfile = () => {
  const dictionary = useDictionary();
  const prevMyProfileData: myProfileData = {
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    phone: '',
    fax: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  };
  const [formData, setFormData] = useState<myProfileData>(prevMyProfileData);
  const boxeverLoaded = useCdpLoaded();
  const { data: sessionData } = useSession();

  const fullName = sessionData?.user?.name ?? '';
  const [firstName, lastName] = fullName.split(' ');

  useEffect(() => {
    if (boxeverLoaded) {
      getSalesforceFlowData().then((res: SalesForceData): void => {
        const salesforceContact = res;

        if (salesforceContact && salesforceContact.profileData) {
          const formCenterData = JSON.parse(
            salesforceContact.profileData.guestCenterData as string
          );

          setFormData({
            firstName: salesforceContact.profileData.guestFName,
            lastName: salesforceContact.profileData.guestLName,
            jobTitle: salesforceContact.profileData.guestJobTitle,
            email: salesforceContact.profileData.guestEmail,
            phone: salesforceContact.profileData.guestPhone,
            fax: salesforceContact.profileData.guestFax,
            company: formCenterData[0]?.centerName as string,
            address: formCenterData[0]?.centerStreet as string,
            city: formCenterData[0]?.centerCity as string,
            state: formCenterData[0]?.centerState as string,
            zipCode: formCenterData[0]?.centerZip as string,
          });
        }
      });
    }
  }, [boxeverLoaded, sessionData?.user?.name]);

  const tableTrCss = classnames('odd:bg-gray-whisper', 'even:bg-white', 'text-solidBlack');
  const tableTdLeftCss = classnames('w-2/5', 'p-2', 'font-medium', 'align-top');
  const tableTdRightCss = classnames('w-3/5', 'p-2');
  const tableHeadLine = classnames('heading-lg', 'text-left', 'mb-4');
  const fullWidthCss = classnames('w-full');
  const tableColCss = classnames('w-full', 'pb-10', 'md:w-1/2', 'md:pb-0');

  const leftCol = (
    // Contact Information
    <>
      <h2 className={tableHeadLine}>{dictionary.myprofile_contact_info}</h2>
      <table className={fullWidthCss}>
        <tbody>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.checkout_first_name}</td>
            <td className={tableTdRightCss}>
              {formData.firstName !== '' ? formData.firstName : firstName}
            </td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.checkout_last_name}</td>
            <td className={tableTdRightCss}>
              {formData.lastName !== '' ? formData.lastName : lastName}
            </td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.myprofile_job_title}</td>
            <td className={tableTdRightCss}>{formData.jobTitle}</td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.myprofile_email}</td>
            <td className={tableTdRightCss}>{formData.email}</td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.myprofile_phone}</td>
            <td className={tableTdRightCss}>{formData.phone}</td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.myprofile_fax}</td>
            <td className={tableTdRightCss}>{formData.fax}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
  const rightCol = (
    // Company Information
    <>
      <h2 className={tableHeadLine}>{dictionary.myprofile_company_info}</h2>
      <table className={fullWidthCss}>
        <tbody>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.checkout_company}</td>
            <td className={tableTdRightCss}>{formData.company}</td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.checkout_address}</td>
            <td className={tableTdRightCss}>{formData.address}</td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.checkout_city}</td>
            <td className={tableTdRightCss}>{formData.city}</td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.checkout_state}</td>
            <td className={tableTdRightCss}>{formData.state}</td>
          </tr>
          <tr className={tableTrCss}>
            <td className={tableTdLeftCss}>{dictionary.checkout_zip_code}</td>
            <td className={tableTdRightCss}>{formData.zipCode}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
  const leftColLogin = (
    // Login Details
    <>
      <div className={classnames('md:pt-16')}>
        <h2 className={tableHeadLine}>{dictionary.myprofile_login_details}</h2>
        <table className={fullWidthCss}>
          <tbody>
            <tr className={tableTrCss}>
              <td className={tableTdLeftCss}>{dictionary.myprofile_loginid}</td>
              <td className={tableTdRightCss}>{sessionData?.user?.email}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div data-component="authorable/utility-nav/myprofile">
      <div className={classnames('w-full', 'flex', 'flex-col', 'md:flex-row')}>
        <div className={classnames(tableColCss, 'md:pr-[6%]')}>{leftCol}</div>
        <div className={classnames(tableColCss, 'md:pl-[6%]')}>{rightCol}</div>
      </div>
      <div className={classnames('w-full')}>
        <div className={classnames(tableColCss, 'md:pr-[6%]')}>{leftColLogin}</div>
      </div>
    </div>
  );
};

export default MyProfile;
